import { Box, Button, ScrollArea, Stack, Text } from '@mantine/core';
import { Dropzone, type DropzoneProps } from '@mantine/dropzone';
import { FileIcon, TrashIcon } from 'icons';
import { useState } from 'react';
import {
  type FieldValues,
  type UseFieldArrayProps,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { humanFileSize } from 'shared/helpers';
import { useShowNotification } from 'shared/hooks/notification/use-show-notification';
import { Description } from 'shared/ui/FileDropzone/Description/Description';
import { FormError } from 'shared/ui/Form';

export type FileFieldProps<T extends FieldValues> = UseFieldArrayProps<T> &
  Omit<DropzoneProps, 'onFileDrop' | 'onDrop' | 'children'> & {
    maxFileSize?: number;
    acceptText?: string;
    additionalText?: string;
    verifyFile?: (file: any) => Promise<void>;
    onRemove?: (file: any) => void;
    icon?: React.ReactNode;
    label?: string;
  };

/**
 * FileField component
 *
 * @example
 * ```tsx
 * <FileField name="field.name" required multiple />
 * ```
 *
 * @public
 * @param name - field name
 * @param control - form control
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param multiple - allow multiple files to be uploaded
 * @param maxFileSize - max file size in bytes
 * @param acceptText - text to display when file is accepted
 * @param additionalText - text to display below the dropzone
 * @param verifyFile - function to verify file before uploading. If rejected, file will not be uploaded.
 * @param onRemove - function to handle file removal
 * @param icon - icon to display in the dropzone
 * @param label - label to display above the dropzone
 * @param props - other props
 */
export function FileDropzoneField<T extends FieldValues>({
  name,
  control,
  rules,
  shouldUnregister,
  multiple,
  maxFileSize = 20000000,
  acceptText = '',
  additionalText = '',
  verifyFile,
  onRemove,
  icon,
  label,
  ...props
}: FileFieldProps<T>) {
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);
  const showNotification = useShowNotification();
  const { fields, append, remove } = useFieldArray({
    name,
    control,
    rules,
    shouldUnregister,
  });
  const { clearErrors } = useFormContext();

  const mapCode = (code: string, fileName: string) => {
    const maxSize = humanFileSize(maxFileSize, true);

    switch (code) {
      case 'file-too-large':
        return t('formErrors.fileTooLargeWithName', { fileName, maxSize });
      default:
        return code;
    }
  };

  /**
   * Method to handle file drop and verification
   * @param files
   */
  const onDrop = async (files: File[]) => {
    if (!files.length) {
      return;
    }

    clearErrors(name);

    const scope = multiple ? files : [files[0]];

    if (verifyFile) {
      setLoading(true);

      try {
        // Verify per-file so one rejected file does not discard the whole drop:
        // append only the ones that passed verification.
        const results = await Promise.allSettled(scope.map(verifyFile));
        scope.forEach((file, index) => {
          if (results[index].status === 'fulfilled') {
            append(file as any);
          }
        });
      } finally {
        setLoading(false);
      }
    } else {
      scope.forEach(file => {
        append(file as any);
      });
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        gap: '8px',
        overflow: 'hidden',
      }}
    >
      <Stack spacing={4}>
        {label && (
          <Text size="lg" fw={500}>
            {label}
          </Text>
        )}
        <FormError name={`${name}`} />
        {(multiple || (!multiple && fields.length === 0)) && (
          <Dropzone
            loading={loading}
            sx={theme => ({
              padding: theme.other.spacing(2),
              textAlign: 'center',
            })}
            multiple={multiple}
            useFsAccessApi={false}
            onReject={error => {
              error.forEach(fileRejection => {
                fileRejection.errors.forEach(({ code }) => {
                  showNotification({
                    variant: 'error',
                    message: mapCode(code, fileRejection.file.name),
                  });
                });
              });
            }}
            onDrop={onDrop}
            maxSize={maxFileSize}
            {...props}
          >
            <Description
              isLoading={false}
              acceptText={acceptText}
              additionalText={additionalText}
              icon={icon}
            />
          </Dropzone>
        )}
      </Stack>
      <ScrollArea h="100%">
        <Stack spacing={4}>
          {fields.map((field: any, index) => (
            <Stack key={field.id} spacing={0}>
              <Box
                key={field.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <FileIcon />
                <Text>{field.path}</Text>
                <Button
                  variant="subtle"
                  onClick={() => {
                    clearErrors(`${name}.${index}`);
                    onRemove?.(field);
                    remove(index);
                  }}
                >
                  <TrashIcon color="red" />
                </Button>
              </Box>
              <FormError name={`${name}.${index}`} />
            </Stack>
          ))}
        </Stack>
      </ScrollArea>
    </Box>
  );
}
